import { ChangeDetectionStrategy, Component, computed, inject, input, output, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../../services/language.service';
import { ParsedUrl, parseUrl } from '../../../utils/url-parser/url-parser.utils';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { of, switchMap } from 'rxjs';

@Component({
    selector: 'tran-base-custom-link',
    imports: [TranslatePipe],
    templateUrl: './base-custom-link.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BaseCustomLinkComponent {
    private readonly translate = inject(TranslateService);
    private readonly languageService = inject(LanguageService);
    private readonly router = inject(Router);

    public readonly url = input.required<string>();
    public readonly label = input<string>();
    public readonly ariaLabel = input<string>();
    public readonly blankTab = input<boolean>(false);
    public readonly fragment = input<string>();
    public readonly clicked = output<void>();

    protected readonly translatedUrl = toSignal(
        toObservable(this.url).pipe(switchMap(url => (url ? this.translate.stream(url) : of('')))),
        { initialValue: '' }
    );

    protected readonly urlPartsSignal: Signal<ParsedUrl> = computed(() =>
        parseUrl(this.translatedUrl() || '', this.fragment())
    );

    protected linkClicked(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.clicked.emit();
        this.navigate();
    }

    private navigate(): void {
        if (this.urlPartsSignal().isInternal) {
            const urlParts = this.urlPartsSignal();
            const finalQueryParams = { ...urlParts.queryParams };

            this.router.navigate([urlParts.path], {
                fragment: urlParts.fragment || undefined,
                queryParamsHandling: 'merge',
                queryParams: finalQueryParams
            });
        } else {
            window.open(this.translatedUrl(), '_blank', 'noopener noreferrer');
        }
    }
}
